import { CircleAlert, ShieldAlert, ShieldCheck } from "lucide-react";
import type { MapSliceCoverage } from "@/features/explorer/api/contracts";
import { cn } from "@/lib/utils";

type CoverageBadgeProps = {
  coverage: MapSliceCoverage;
  suppressedCount: number;
  className?: string;
};

function percent(covered: number, total: number): string {
  if (total <= 0) return "—";
  return `${Math.round((covered / total) * 100)}%`;
}

const COVERAGE_TIERS = [
  { min: 95, cls: "border-success/40 bg-success/10 text-success", label: "excellent coverage" },
  { min: 80, cls: "border-warning/40 bg-warning/10 text-warning", label: "good coverage" },
  { min: 50, cls: "border-orange-500/40 bg-orange-500/10 text-orange-500", label: "partial coverage" },
  { min: 0, cls: "border-destructive/40 bg-destructive/10 text-destructive", label: "low coverage" },
];

function coverageTier(ratio: number): { cls: string; label: string } {
  const percentRatio = ratio * 100;
  // The 0% tier always matches, so `find` is guaranteed to resolve; the
  // fallback is only to satisfy the type checker.
  return (
    COVERAGE_TIERS.find((tier) => percentRatio >= tier.min) ?? {
      cls: "border-destructive/40 bg-destructive/10 text-destructive",
      label: "low coverage",
    }
  );
}

/**
 * Small trust / provenance summary shown with a metric. Surfaces how much of
 * the geography is actually reported (vs absent) and how many figures are
 * suppressed for confidentiality or reliability. This is driven by the
 * `coverage` metadata the API attaches to every map slice.
 *
 * Colour reflects the share of geographies reported (covered/total):
 * ≥95% green, ≥80% yellow, 50–80% orange, <50% red.
 */
export function CoverageBadge({ coverage, suppressedCount, className }: CoverageBadgeProps) {
  const missing = coverage.totalGeographies - coverage.coveredGeographies;
  const hasMissing = missing > 0;
  const hasSuppressed = suppressedCount > 0;
  const ratio = coverage.totalGeographies > 0 ? coverage.coveredGeographies / coverage.totalGeographies : 0;
  const tier = coverageTier(ratio);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/80 pt-2.5 font-sans text-sm",
        className,
      )}>
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-xs border px-1.5 py-0.5 text-xs font-semibold",
          tier.cls,
        )}
        title={
          hasMissing
            ? `${coverage.coveredGeographies} of ${coverage.totalGeographies} ${coverage.coveredGeographies === 1 ? "area" : "areas"} reported (${tier.label}); ${missing} without data.`
            : `All ${coverage.totalGeographies} ${coverage.totalGeographies === 1 ? "area" : "areas"} reported (${tier.label}).`
        }>
        {hasMissing ? <CircleAlert className='size-3' /> : <ShieldCheck className='size-3' />}
        <span>
          {coverage.coveredGeographies}/{coverage.totalGeographies} areas ·{" "}
          {percent(coverage.coveredGeographies, coverage.totalGeographies)}
        </span>
      </span>

      {hasSuppressed && (
        <span
          className='inline-flex items-center gap-1.5 rounded-xs border border-muted-foreground/30 bg-muted/40 px-1.5 py-0.5 text-xs font-semibold text-muted-foreground'
          title={`${suppressedCount} ${suppressedCount === 1 ? "figure" : "figures"} suppressed for confidentiality or reliability and not shown on the map.`}>
          <ShieldAlert className='size-3' />
          <span>{suppressedCount} suppressed</span>
        </span>
      )}
    </div>
  );
}
